/**
 * キャリコネクション（グローバルウェイ）送客の純関数群。
 *
 * - varuna に渡すリクエスト本文の組み立て（①媒体取得 / ②登録 共通）
 * - ①の返却媒体を RT ダイアログ / DL RT バナーの表示情報へ写す
 * - rt_condition 行の判定（api_endpoint_key）
 *
 * 副作用（axios / emitter）は services / composables 側に置く。保存書式（localforage）の
 * 読み込みは careerconnectionStorage.ts。
 */
import { expectedDocumentTypeForFormat } from '@/utils/format-edit/formatDetailBackup'

/** rt_condition.api_endpoint_key（rtConsultDispatch のキーと同じ文字列） */
export const CAREERCONNECTION_ENDPOINT_KEY = 'sendCareerconnectionApi'
export const CAREERCONNECTION_DIALOG_TYPE = 'open-careerconnection-dialog'
export const CAREERCONNECTION_CONSULT_EVENT = 'send-careerconnection-consult'

/** ①媒体取得APIが返す媒体1件（varuna が整形済み） */
export interface CcMedia {
  alliance_key: string
  alliance_name: string
  overview?: string
  introduction?: string
  terms_url?: string
  privacy_url?: string
  logo_img_url?: string
}

type UnknownRecord = Record<string, any>

/** ダイアログ / バナーの表示情報に混ぜる媒体フィールド（cc プレフィックス） */
export interface CcMediaDialogFields {
  ccAllianceKey: string
  ccMediaName: string
  ccMediaLogo: string
  ccMediaTerms: string
  ccMediaPrivacy: string
  ccOverview: string
  ccIntroduction: string
}

export interface CcRequestContext {
  uuid: string
  clientId: string
  permissionType: number
  conditionId: number | null
  formatId: string
  dialogName: string
  isMobile: boolean
  appFlag: string
  isMypage: boolean
}

export interface CcFormats {
  basicData: UnknownRecord
  formatRirekisho: UnknownRecord | null
  formatShokumukeirekisho: UnknownRecord | null
}

/** ①②共通のリクエスト本文（プラン §11 の契約） */
export function buildCareerconnectionRequestBody(
  ctx: CcRequestContext,
  data: CcFormats
): UnknownRecord {
  return {
    uuid: ctx.uuid,
    client_id: ctx.clientId,
    permission_type: Number(ctx.permissionType) || 0,
    condition_id: ctx.conditionId == null ? null : Number(ctx.conditionId),
    format_id: ctx.formatId || '',
    dialog_name: ctx.dialogName || '',
    device_flg: ctx.isMobile ? 'SP' : 'PC',
    app_flag: ctx.appFlag || '',
    is_mypage: ctx.isMypage === true,
    basicData: data.basicData || {},
    format_rirekisho: data.formatRirekisho || {},
    format_shokumukeirekisho: data.formatShokumukeirekisho || null
  }
}

/**
 * AUTO_RT ペイロードの formatData / otherFormatData を書式種別で振り分ける。
 * otherFormatData は「相手書式そのもの」（FormatEdit）と
 * `{ format_rirekisho / format_shokumukeirekisho }` のキー付き（マイページ等）の両形を受ける
 */
export function splitFormatDataByFormatId(
  formatId: string,
  formatData: unknown,
  otherFormatData: unknown
): Pick<CcFormats, 'formatRirekisho' | 'formatShokumukeirekisho'> {
  const fd = isRecord(formatData) ? formatData : null
  // skillsheet_* も職務経歴書側（既存の書式分類ヘルパーに合わせる）
  const isShoku =
    expectedDocumentTypeForFormat(String(formatId || '')) ===
    'shokumukeirekisho'
  const other = unwrapOtherFormatData(
    otherFormatData,
    isShoku ? 'format_rirekisho' : 'format_shokumukeirekisho'
  )
  if (isShoku) {
    return { formatRirekisho: other, formatShokumukeirekisho: fd }
  }
  return { formatRirekisho: fd, formatShokumukeirekisho: other }
}

function unwrapOtherFormatData(
  other: unknown,
  key: 'format_rirekisho' | 'format_shokumukeirekisho'
): UnknownRecord | null {
  if (!isRecord(other)) return null
  const nested = other[key]
  if (isRecord(nested)) return nested
  // キー付きの器なのに相手書式が無い（{format_xxx: null} 等）場合は空扱い
  if ('format_rirekisho' in other || 'format_shokumukeirekisho' in other) {
    return null
  }
  return Object.keys(other).length > 0 ? other : null // {} と未保存(null)を同じ指紋に
}

/**
 * ①判定のキャッシュ鍵に混ぜる指紋。①リクエスト本文のうちサーバーが判定に使う部分
 * （basicData / format_rirekisho / format_shokumukeirekisho）を、キー順を固定した JSON にして
 * FNV-1a でハッシュする。履歴書のどこを編集しても再判定させるのが目的で、衝突耐性は要らない。
 */
export function computeSnapshotFingerprint(
  basicData: UnknownRecord,
  formatRirekisho: UnknownRecord | null,
  formatShokumukeirekisho: UnknownRecord | null
): string {
  const seed = stableStringify({
    basicData: basicData || {},
    format_rirekisho: formatRirekisho || {},
    format_shokumukeirekisho: formatShokumukeirekisho || null
  })
  let hash = 0x811c9dc5
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(36)
}

/** キー順を固定した JSON.stringify（undefined / 関数は JSON と同じく落とす） */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'null'
  }
  if (Array.isArray(value)) {
    return '[' + value.map((v) => stableStringify(v)).join(',') + ']'
  }
  const record = value as UnknownRecord
  const keys = Object.keys(record)
    .filter((k) => record[k] !== undefined && typeof record[k] !== 'function')
    .sort()
  return (
    '{' +
    keys
      .map((k) => JSON.stringify(k) + ':' + stableStringify(record[k]))
      .join(',') +
    '}'
  )
}

/** $deviceLib.isMobileDevice は Vue computed（boolean）。'SP' 文字列の旧比較にも耐える */
export function resolveDeviceIsMobile(deviceLib: unknown): boolean {
  const raw = isRecord(deviceLib) ? deviceLib.isMobileDevice : undefined
  const value = typeof raw === 'function' ? raw() : raw
  return value === true || value === 'SP' || value === 'sp'
}

export function isCareerconnectionRow(row: unknown): boolean {
  return (
    isRecord(row) &&
    String(row.api_endpoint_key || '').trim() === CAREERCONNECTION_ENDPOINT_KEY
  )
}

/** 条件行の参照（condition_id があればそれ、無ければ client_id:permission_type） */
export interface CcRowRef {
  clientId: string
  permissionType: number
  conditionId: number | null
}

export function rowRefFromCondition(row: UnknownRecord): CcRowRef {
  return {
    clientId: String(row.client_id || ''),
    permissionType: Number(row.permission_type) || 0,
    conditionId: row.condition_id == null ? null : Number(row.condition_id)
  }
}

export function ccCacheKey(uid: string, ref: CcRowRef): string {
  const rowKey =
    ref.conditionId != null
      ? `cond:${ref.conditionId}`
      : `${ref.clientId}:${ref.permissionType}`
  return `${uid || 'anon'}|${rowKey}`
}

export function mediaToDialogFields(media: CcMedia): CcMediaDialogFields {
  return {
    ccAllianceKey: String(media.alliance_key || ''),
    ccMediaName: String(media.alliance_name || ''),
    ccMediaLogo: String(media.logo_img_url || ''),
    ccMediaTerms: String(media.terms_url || ''),
    ccMediaPrivacy: String(media.privacy_url || ''),
    ccOverview: String(media.overview || ''),
    ccIntroduction: String(media.introduction || '')
  }
}

/**
 * DL RT バナー用の partner_terms_text。
 *
 * バナーの文言は useRtTextFormat（BudouX）を通るため生 HTML を渡せない。
 * 既存のリンクトークン記法（rtPartnerTermsText.ts）を使い、行の [terms]/[privacy] は
 * キャリコネ（termurl/privacyurl）、媒体側は handlingurl/occupationurl のスロットへ
 * ラベル付きトークンで差し込む。
 */
export function buildMediaPartnerTerms(
  media: CcMedia,
  clientServiceName: string
): {
  partner_terms_text: string
  handlingurl: string
  occupationurl: string
} {
  const cc = clientServiceName || 'キャリコネ転職'
  const name = (media.alliance_name || '連携先媒体').replace(/[<>]/g, '') // v-html に入る
  return {
    partner_terms_text:
      `${cc}の[terms]・[privacy]、${name}の[handling:利用規約]・` +
      `[occupation:個人情報保護方針]を確認し同意した上で、` +
      `キャリコネ経由で${name}に履歴書情報を送客します`,
    handlingurl: String(media.terms_url || ''),
    occupationurl: String(media.privacy_url || '')
  }
}

function isRecord(value: unknown): value is UnknownRecord {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}
